import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHotel, faMagnifyingGlass, faBed, faUser, faClock, faWrench, faCircleCheck, faCheck } from "@fortawesome/free-solid-svg-icons";
import DashboardLayout from "../layout/DashboardLayout";
import "../layout/Dashboard.css";

const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:5050";

// ── Types ──────────────────────────────────────────────────────
interface RoomTask {
  taskId: number | null;
  roomId: string;
  roomNumber: string;
  roomType: string;
  floor: string;
  status: "dirty" | "cleaning" | "clean" | "inspected" | "out-of-service";
  priority: "urgent" | "normal" | "low";
  assignedTo: string;
  notes: string;
  inspectedBy: string;
  checkedAt: string | null;
}

interface HKStats {
  dirty: number;
  cleaning: number;
  clean: number;
  inspected: number;
  outOfService: number;
  total: number;
}

// ── Status / Priority meta ─────────────────────────────────────
const STATUS_META: Record<string, { label: string; color: string; bg: string; icon: string }> = {
  dirty:          { label: "Dirty",          color: "#ef4444", bg: "rgba(239,68,68,0.12)",    icon: "dirty" },
  cleaning:       { label: "Cleaning",       color: "#f59e0b", bg: "rgba(245,158,11,0.12)",   icon: "cleaning" },
  clean:          { label: "Clean",          color: "#10b981", bg: "rgba(16,185,129,0.12)",   icon: "clean" },
  inspected:      { label: "Inspected",      color: "#6366f1", bg: "rgba(99,102,241,0.12)",   icon: "inspected" },
  "out-of-service": { label: "Out of Service", color: "rgba(242,235,217,0.4)", bg: "rgba(100,116,139,0.12)", icon: "out-of-service" },
};

function StatusIcon({ status }: { status: string }) {
  if (status === "inspected") return <FontAwesomeIcon icon={faCircleCheck} />;
  if (status === "out-of-service") return <FontAwesomeIcon icon={faWrench} />;
  const dotColor = status === "dirty" ? "#ef4444" : status === "cleaning" ? "#f59e0b" : "#10b981";
  return <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: dotColor }} />;
}

const PRIORITY_COLOR: Record<string, string> = {
  urgent: "#ef4444",
  normal: "#94a3b8",
  low:    "#475569",
};

const STATUS_CYCLE: Record<string, RoomTask["status"]> = {
  dirty:    "cleaning",
  cleaning: "clean",
  clean:    "inspected",
  inspected: "dirty",
  "out-of-service": "dirty",
};

// ── Shared input styles (inline, consistent with other pages) ──
const FS: React.CSSProperties = {
  background: "#0E1E38",
  border: "1px solid #e2e8f0",
  borderRadius: 10,
  padding: "10px 14px",
  color: "#FDFAF4",
  fontSize: 14,
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
};

const LABEL: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.8px",
  color: "rgba(242,235,217,0.45)",
  display: "block",
  marginBottom: 4,
};

function fmtTime(iso: string | null) {
  if (!iso) return null;
  const d = new Date(iso);
  return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) +
         " · " +
         d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}

// ── Room Card ──────────────────────────────────────────────────
function RoomCard({
  task,
  onOpen,
  onCycle,
}: {
  task: RoomTask;
  onOpen: (t: RoomTask) => void;
  onCycle: (t: RoomTask) => void;
}) {
  const sm = STATUS_META[task.status] ?? STATUS_META.dirty;
  const isUrgent = task.priority === "urgent";
  const nextStatus = STATUS_CYCLE[task.status];
  const nextSm = STATUS_META[nextStatus];

  return (
    <div
      style={{
        background: "#0E1E38",
        border: `1px solid rgba(99,102,241,0.12)`,
        borderRadius: 14,
        padding: "18px 16px 14px",
        display: "flex",
        flexDirection: "column",
        gap: 10,
        cursor: "pointer",
        transition: "border-color 0.2s, box-shadow 0.2s",
        position: "relative",
        overflow: "hidden",
      }}
      onClick={() => onOpen(task)}
      onMouseEnter={e => {
        (e.currentTarget as HTMLDivElement).style.borderColor = "rgba(99,102,241,0.4)";
        (e.currentTarget as HTMLDivElement).style.boxShadow = "0 4px 24px rgba(0,0,0,0.3)";
      }}
      onMouseLeave={e => {
        (e.currentTarget as HTMLDivElement).style.borderColor = "rgba(99,102,241,0.12)";
        (e.currentTarget as HTMLDivElement).style.boxShadow = "none";
      }}
    >
      {/* Status color strip */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: 3,
        background: sm.color, borderRadius: "14px 14px 0 0",
      }} />

      {/* Top row: room number + priority dot */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 4 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
          <span style={{ fontSize: 22, fontWeight: 800, color: "#FDFAF4", lineHeight: 1 }}>
            #{task.roomNumber}
          </span>
          <span style={{ fontSize: 11, color: "rgba(242,235,217,0.45)", fontWeight: 500 }}>Floor {task.floor}</span>
        </div>
        {isUrgent && (
          <div style={{
            width: 8, height: 8, borderRadius: "50%",
            background: "#ef4444",
            boxShadow: "0 0 6px #ef4444",
            flexShrink: 0,
          }} title="Urgent" />
        )}
      </div>

      {/* Room type */}
      <div style={{ fontSize: 12, color: "rgba(242,235,217,0.4)", fontWeight: 500, marginTop: -6 }}>
        {task.roomType || "—"}
      </div>

      {/* Status badge */}
      <div style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        padding: "4px 10px", borderRadius: 20,
        background: sm.bg, color: sm.color,
        fontSize: 11, fontWeight: 700, alignSelf: "flex-start",
        letterSpacing: "0.4px",
      }}>
        <span style={{ fontSize: 10 }}><StatusIcon status={task.status} /></span>
        {sm.label.toUpperCase()}
      </div>

      {/* Assigned to */}
      <div style={{ fontSize: 12, color: task.assignedTo ? "#94a3b8" : "#475569", display: "flex", alignItems: "center", gap: 5 }}>
        <span style={{ fontSize: 11 }}><FontAwesomeIcon icon={faUser} /></span>
        {task.assignedTo || "Unassigned"}
      </div>

      {/* Last updated */}
      {task.checkedAt && (
        <div style={{ fontSize: 11, color: "rgba(242,235,217,0.45)" }}>
          <FontAwesomeIcon icon={faClock} /> {fmtTime(task.checkedAt)}
        </div>
      )}

      {/* Priority label */}
      <div style={{
        fontSize: 10, fontWeight: 700, textTransform: "uppercase",
        letterSpacing: "0.6px", color: PRIORITY_COLOR[task.priority] ?? "#94a3b8",
      }}>
        {task.priority} priority
      </div>

      {/* Action row */}
      <div
        style={{ display: "flex", gap: 6, marginTop: 4 }}
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={() => onCycle(task)}
          title={`Mark as ${nextSm?.label}`}
          style={{
            flex: 1, padding: "7px 0", borderRadius: 8, border: "none",
            background: nextSm?.bg ?? "rgba(201,168,76,0.12)",
            color: nextSm?.color ?? "#818cf8",
            fontSize: 11, fontWeight: 700, cursor: "pointer",
            letterSpacing: "0.3px", textTransform: "uppercase",
            transition: "opacity 0.15s",
          }}
          onMouseEnter={e => (e.currentTarget.style.opacity = "0.8")}
          onMouseLeave={e => (e.currentTarget.style.opacity = "1")}
        >
          → {nextSm?.label}
        </button>
        <button
          onClick={() => onOpen(task)}
          title="Edit"
          style={{
            padding: "7px 12px", borderRadius: 8, border: "1px solid #e2e8f0",
            background: "transparent", color: "rgba(242,235,217,0.45)",
            fontSize: 11, cursor: "pointer",
            transition: "background 0.15s",
          }}
          onMouseEnter={e => (e.currentTarget.style.background = "rgba(201,168,76,0.12)")}
          onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
        >
          Edit
        </button>
      </div>
    </div>
  );
}

// ── Update Modal ───────────────────────────────────────────────
function UpdateModal({
  task,
  authHeaders,
  onClose,
  onSaved,
}: {
  task: RoomTask;
  authHeaders: Record<string, string>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [status, setStatus]           = useState<RoomTask["status"]>(task.status);
  const [priority, setPriority]       = useState<RoomTask["priority"]>(task.priority);
  const [assignedTo, setAssignedTo]   = useState(task.assignedTo ?? "");
  const [notes, setNotes]             = useState(task.notes ?? "");
  const [inspectedBy, setInspectedBy] = useState(task.inspectedBy ?? "");
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState("");

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      const now = new Date().toISOString();
      if (task.taskId) {
        await axios.put(
          `${API_BASE}/api/housekeeping/${task.taskId}`,
          {
            status,
            assigned_to: assignedTo,
            notes,
            inspected_by: status === "inspected" ? inspectedBy : undefined,
            checked_at: now,
          },
          { headers: authHeaders }
        );
      } else {
        await axios.post(
          `${API_BASE}/api/housekeeping`,
          {
            roomid: task.roomId,
            status,
            priority,
            assigned_to: assignedTo,
            notes,
          },
          { headers: authHeaders }
        );
      }
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err?.message ?? "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const STATUSES: RoomTask["status"][] = ["dirty", "cleaning", "clean", "inspected", "out-of-service"];
  const PRIORITIES: RoomTask["priority"][] = ["urgent", "normal", "low"];

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(6,13,31,0.85)", backdropFilter: "blur(4px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#0f172a",
          border: "1px solid rgba(99,102,241,0.25)",
          borderRadius: 18,
          padding: 28,
          width: "100%",
          maxWidth: 480,
          boxShadow: "0 24px 80px rgba(0,0,0,0.7)",
          display: "flex",
          flexDirection: "column",
          gap: 18,
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: "#FDFAF4" }}>
              Room #{task.roomNumber}
            </h2>
            <p style={{ margin: "3px 0 0", fontSize: 12, color: "rgba(242,235,217,0.4)" }}>
              {task.roomType} · Floor {task.floor}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "rgba(100,116,139,0.1)", border: "none", borderRadius: 8,
              width: 32, height: 32, cursor: "pointer", color: "rgba(242,235,217,0.4)",
              fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            &times;
          </button>
        </div>

        {/* Status selector */}
        <div>
          <label style={LABEL}>Status</label>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {STATUSES.map(s => {
              const sm = STATUS_META[s];
              return (
                <button
                  key={s}
                  onClick={() => setStatus(s)}
                  style={{
                    padding: "6px 12px", borderRadius: 20, fontSize: 11,
                    fontWeight: 700, letterSpacing: "0.4px", cursor: "pointer",
                    border: status === s ? `1.5px solid ${sm.color}` : "1px solid rgba(99,102,241,0.15)",
                    background: status === s ? sm.bg : "transparent",
                    color: status === s ? sm.color : "#64748b",
                    textTransform: "uppercase",
                    transition: "all 0.15s",
                  }}
                >
                  <StatusIcon status={s} /> {sm.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Priority selector */}
        <div>
          <label style={LABEL}>Priority</label>
          <div style={{ display: "flex", gap: 6 }}>
            {PRIORITIES.map(p => (
              <button
                key={p}
                onClick={() => setPriority(p)}
                style={{
                  padding: "6px 14px", borderRadius: 20, fontSize: 11,
                  fontWeight: 700, letterSpacing: "0.4px", cursor: "pointer",
                  textTransform: "uppercase",
                  border: priority === p
                    ? `1.5px solid ${PRIORITY_COLOR[p]}`
                    : "1px solid rgba(99,102,241,0.15)",
                  background: priority === p ? `${PRIORITY_COLOR[p]}18` : "transparent",
                  color: priority === p ? PRIORITY_COLOR[p] : "#64748b",
                  transition: "all 0.15s",
                }}
              >
                {p}
              </button>
            ))}
          </div>
        </div>

        {/* Assigned to */}
        <div>
          <label style={LABEL}>Assigned To</label>
          <input
            type="text"
            value={assignedTo}
            onChange={e => setAssignedTo(e.target.value)}
            placeholder="Staff name…"
            style={FS}
          />
        </div>

        {/* Inspected by — only when inspected */}
        {status === "inspected" && (
          <div>
            <label style={LABEL}>Inspected By</label>
            <input
              type="text"
              value={inspectedBy}
              onChange={e => setInspectedBy(e.target.value)}
              placeholder="Inspector name…"
              style={FS}
            />
          </div>
        )}

        {/* Notes */}
        <div>
          <label style={LABEL}>Notes</label>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Any notes for this room…"
            rows={3}
            style={{ ...FS, resize: "vertical", fontFamily: "inherit" }}
          />
        </div>

        {error && (
          <div style={{
            padding: "10px 14px", borderRadius: 8,
            background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.25)",
            color: "#f87171", fontSize: 13,
          }}>
            {error}
          </div>
        )}

        {/* Footer */}
        <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
          <button
            onClick={onClose}
            style={{
              flex: 1, padding: "11px 0", borderRadius: 10, fontSize: 13,
              fontWeight: 600, cursor: "pointer",
              background: "transparent", border: "1px solid #e2e8f0",
              color: "rgba(242,235,217,0.4)", transition: "background 0.15s",
            }}
            onMouseEnter={e => (e.currentTarget.style.background = "rgba(99,102,241,0.06)")}
            onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              flex: 2, padding: "11px 0", borderRadius: 10, fontSize: 13,
              fontWeight: 700, cursor: saving ? "not-allowed" : "pointer",
              background: saving ? "rgba(14,31,53,0.3)" : "#0e1f35",
              border: "none", color: "#FDFAF4", transition: "background 0.15s",
            }}
          >
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────
export default function HousekeepingPage() {
  const token = localStorage.getItem("ai2_token") ?? "";
  const hotel = localStorage.getItem("ai2_hotel") ?? "";
  const authHeaders = { Authorization: `Bearer ${token}`, "x-hotel": hotel };

  const [tasks, setTasks]             = useState<RoomTask[]>([]);
  const [stats, setStats]             = useState<HKStats | null>(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState("");

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [search, setSearch]             = useState("");
  const [sortBy, setSortBy]             = useState<"priority" | "roomNumber" | "status">("priority");

  // Modal
  const [modalTask, setModalTask] = useState<RoomTask | null>(null);

  // Mark-all-clean state
  const [markingClean, setMarkingClean] = useState(false);
  const [successMsg, setSuccessMsg]     = useState("");

  // Auto-refresh
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchAll = useCallback(async () => {
    try {
      const [tasksRes, statsRes] = await Promise.all([
        axios.get(`${API_BASE}/api/housekeeping`, { headers: authHeaders }),
        axios.get(`${API_BASE}/api/housekeeping/stats`, { headers: authHeaders }),
      ]);
      setTasks(Array.isArray(tasksRes.data) ? tasksRes.data : []);
      setStats(statsRes.data ?? null);
      setError("");
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err?.message ?? "Failed to load housekeeping data");
    } finally {
      setLoading(false);
    }
  }, [token, hotel]);

  useEffect(() => {
    fetchAll();
    timerRef.current = setInterval(fetchAll, 60_000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [fetchAll]);

  // Cycle status quick action
  const handleCycle = async (task: RoomTask) => {
    const nextStatus = STATUS_CYCLE[task.status];
    // Optimistic update — show change instantly
    setTasks(prev => prev.map(t => t.roomId === task.roomId ? { ...t, status: nextStatus } : t));
    try {
      const now = new Date().toISOString();
      if (task.taskId) {
        await axios.put(
          `${API_BASE}/api/housekeeping/${task.taskId}`,
          { status: nextStatus, checked_at: now },
          { headers: authHeaders }
        );
      } else {
        const res = await axios.post(
          `${API_BASE}/api/housekeeping`,
          { roomid: task.roomId, status: nextStatus, priority: task.priority, assigned_to: task.assignedTo, notes: task.notes },
          { headers: authHeaders }
        );
        // Store the newly created taskId so future cycles use PUT
        const newId = res.data?.id ?? null;
        if (newId) {
          setTasks(prev => prev.map(t => t.roomId === task.roomId ? { ...t, taskId: newId } : t));
        }
      }
      fetchAll();
    } catch (err: any) {
      // Revert on failure
      setTasks(prev => prev.map(t => t.roomId === task.roomId ? { ...t, status: task.status } : t));
      setError(err?.response?.data?.error ?? err?.message ?? "Failed to update room status");
      setTimeout(() => setError(""), 4000);
    }
  };

  // Mark all clean
  const handleMarkAllClean = async () => {
    const dirty = tasks.filter(t => t.status === "dirty" || t.status === "cleaning");
    if (dirty.length === 0) return;
    if (!window.confirm(`Mark ${dirty.length} room(s) as Clean?`)) return;
    const dirtyIds = new Set(dirty.map(t => t.roomId));
    setTasks(prev => prev.map(t => dirtyIds.has(t.roomId) ? { ...t, status: "clean" } : t));
    setMarkingClean(true);
    try {
      const now = new Date().toISOString();
      await Promise.all(
        dirty.map(t =>
          t.taskId
            ? axios.put(`${API_BASE}/api/housekeeping/${t.taskId}`, { status: "clean", checked_at: now }, { headers: authHeaders })
            : axios.post(`${API_BASE}/api/housekeeping`, { roomid: t.roomId, status: "clean", priority: t.priority, assigned_to: t.assignedTo, notes: t.notes }, { headers: authHeaders })
        )
      );
      setSuccessMsg(`${dirty.length} room(s) marked as Clean.`);
      setTimeout(() => setSuccessMsg(""), 4000);
      fetchAll();
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err?.message ?? "Failed to mark rooms clean");
      setTimeout(() => setError(""), 4000);
      fetchAll(); // reload to get accurate state
    } finally {
      setMarkingClean(false);
    }
  };

  // Filtering + sorting
  const PRIORITY_ORDER: Record<string, number> = { urgent: 0, normal: 1, low: 2 };
  const STATUS_ORDER: Record<string, number>   = { dirty: 0, cleaning: 1, "out-of-service": 2, clean: 3, inspected: 4 };

  const filtered = useMemo(() => {
    let list = [...tasks];

    if (statusFilter !== "all") {
      list = list.filter(t => t.status === statusFilter);
    }

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        t =>
          t.roomNumber.toLowerCase().includes(q) ||
          (t.roomType ?? "").toLowerCase().includes(q) ||
          (t.assignedTo ?? "").toLowerCase().includes(q)
      );
    }

    list.sort((a, b) => {
      const rA = String(a.roomNumber ?? "");
      const rB = String(b.roomNumber ?? "");
      if (sortBy === "priority") {
        const pd = (PRIORITY_ORDER[a.priority] ?? 99) - (PRIORITY_ORDER[b.priority] ?? 99);
        if (pd !== 0) return pd;
        return rA.localeCompare(rB, undefined, { numeric: true });
      }
      if (sortBy === "status") {
        const sd = (STATUS_ORDER[a.status] ?? 99) - (STATUS_ORDER[b.status] ?? 99);
        if (sd !== 0) return sd;
        return rA.localeCompare(rB, undefined, { numeric: true });
      }
      return rA.localeCompare(rB, undefined, { numeric: true });
    });

    return list;
  }, [tasks, statusFilter, search, sortBy]);

  // ── Render ─────────────────────────────────────────────────
  const statCards = [
    { key: "dirty",          value: stats?.dirty          ?? 0, ...STATUS_META.dirty },
    { key: "cleaning",       value: stats?.cleaning       ?? 0, ...STATUS_META.cleaning },
    { key: "clean",          value: stats?.clean          ?? 0, ...STATUS_META.clean },
    { key: "inspected",      value: stats?.inspected      ?? 0, ...STATUS_META.inspected },
    { key: "out-of-service", value: stats?.outOfService   ?? 0, ...STATUS_META["out-of-service"] },
  ];

  return (
    <DashboardLayout>
      <div style={{ padding: "24px 28px", minHeight: "100vh", background: "transparent" }}>

        {/* ── Page Header ── */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 28, flexWrap: "wrap", gap: 12 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: "#FDFAF4", letterSpacing: "-0.4px" }}>
              <FontAwesomeIcon icon={faHotel} style={{ marginRight: 8 }} /> Housekeeping
            </h1>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: "rgba(242,235,217,0.45)" }}>
              {stats?.total ?? tasks.length} rooms · Auto-refreshes every 60 s
            </p>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            {successMsg && (
              <div style={{
                padding: "8px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600,
                background: "rgba(16,185,129,0.12)", border: "1px solid rgba(16,185,129,0.3)",
                color: "#34d399",
              }}>
                <FontAwesomeIcon icon={faCheck} /> {successMsg}
              </div>
            )}
            <button
              onClick={fetchAll}
              style={{
                padding: "9px 14px", borderRadius: 9, border: "1px solid #e2e8f0",
                background: "transparent", color: "rgba(242,235,217,0.45)", fontSize: 13, cursor: "pointer",
                fontWeight: 600, transition: "background 0.15s",
              }}
              onMouseEnter={e => (e.currentTarget.style.background = "rgba(201,168,76,0.12)")}
              onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
            >
              ↻ Refresh
            </button>
            <button
              onClick={handleMarkAllClean}
              disabled={markingClean}
              style={{
                padding: "9px 16px", borderRadius: 9, border: "none",
                background: markingClean ? "rgba(16,185,129,0.25)" : "rgba(16,185,129,0.15)",
                color: "#34d399", fontSize: 13, fontWeight: 700, cursor: markingClean ? "not-allowed" : "pointer",
                transition: "background 0.15s",
              }}
              onMouseEnter={e => { if (!markingClean) e.currentTarget.style.background = "rgba(16,185,129,0.25)"; }}
              onMouseLeave={e => { if (!markingClean) e.currentTarget.style.background = "rgba(16,185,129,0.15)"; }}
            >
              {markingClean ? "Marking…" : <><FontAwesomeIcon icon={faCheck} /> Mark All Clean</>}
            </button>
          </div>
        </div>

        {/* ── Stats Strip ── */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(5, 1fr)",
          gap: 12,
          marginBottom: 24,
        }}>
          {statCards.map(sc => (
            <div
              key={sc.key}
              onClick={() => setStatusFilter(statusFilter === sc.key ? "all" : sc.key)}
              style={{
                background: statusFilter === sc.key ? sc.bg : "#0E1E38",
                border: `1px solid ${statusFilter === sc.key ? sc.color + "50" : "rgba(201,168,76,0.12)"}`,
                borderRadius: 12,
                padding: "16px 14px",
                cursor: "pointer",
                transition: "all 0.2s",
                textAlign: "center",
              }}
              onMouseEnter={e => {
                if (statusFilter !== sc.key) {
                  (e.currentTarget as HTMLDivElement).style.borderColor = sc.color + "40";
                  (e.currentTarget as HTMLDivElement).style.background = sc.bg;
                }
              }}
              onMouseLeave={e => {
                if (statusFilter !== sc.key) {
                  (e.currentTarget as HTMLDivElement).style.borderColor = "rgba(201,168,76,0.12)";
                  (e.currentTarget as HTMLDivElement).style.background = "#0E1E38";
                }
              }}
            >
              <div style={{ fontSize: 20, marginBottom: 4 }}><StatusIcon status={sc.key} /></div>
              <div style={{ fontSize: 26, fontWeight: 800, color: sc.color, lineHeight: 1 }}>{sc.value}</div>
              <div style={{ fontSize: 11, color: "rgba(242,235,217,0.45)", fontWeight: 600, marginTop: 4, textTransform: "uppercase", letterSpacing: "0.6px" }}>
                {sc.label}
              </div>
            </div>
          ))}
        </div>

        {/* ── Filter Bar ── */}
        <div style={{
          display: "flex", gap: 10, marginBottom: 22, flexWrap: "wrap", alignItems: "center",
        }}>
          {/* Search */}
          <div style={{ position: "relative", flex: "1 1 220px", minWidth: 180 }}>
            <span style={{
              position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)",
              color: "rgba(242,235,217,0.45)", fontSize: 13, pointerEvents: "none",
            }}><FontAwesomeIcon icon={faMagnifyingGlass} /></span>
            <input
              type="text"
              placeholder="Search room, type, staff…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ ...FS, paddingLeft: 34, height: 38 }}
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                style={{
                  position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
                  background: "none", border: "none", color: "rgba(242,235,217,0.45)", cursor: "pointer", fontSize: 14,
                }}
              >
                &times;
              </button>
            )}
          </div>

          {/* Status filter pills */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {["all", "dirty", "cleaning", "clean", "inspected"].map(s => {
              const sm = s === "all" ? null : STATUS_META[s];
              const active = statusFilter === s;
              return (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  style={{
                    padding: "7px 13px", borderRadius: 20, fontSize: 11,
                    fontWeight: 700, letterSpacing: "0.4px", cursor: "pointer",
                    textTransform: "uppercase",
                    border: active
                      ? `1.5px solid ${sm?.color ?? "#818cf8"}`
                      : "1px solid rgba(99,102,241,0.15)",
                    background: active ? (sm?.bg ?? "rgba(99,102,241,0.12)") : "transparent",
                    color: active ? (sm?.color ?? "#818cf8") : "#64748b",
                    transition: "all 0.15s",
                  }}
                >
                  {sm ? <><StatusIcon status={s} />{" "}</> : ""}{s === "all" ? "All Rooms" : sm!.label}
                </button>
              );
            })}
          </div>

          {/* Sort */}
          <div style={{ display: "flex", alignItems: "center", gap: 7, marginLeft: "auto" }}>
            <span style={{ fontSize: 11, color: "rgba(242,235,217,0.45)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.6px", whiteSpace: "nowrap" }}>
              Sort:
            </span>
            {(["priority", "roomNumber", "status"] as const).map(s => (
              <button
                key={s}
                onClick={() => setSortBy(s)}
                style={{
                  padding: "7px 12px", borderRadius: 8, fontSize: 11, fontWeight: 600,
                  cursor: "pointer", textTransform: "capitalize",
                  border: sortBy === s ? "1.5px solid rgba(99,102,241,0.5)" : "1px solid rgba(99,102,241,0.15)",
                  background: sortBy === s ? "rgba(99,102,241,0.12)" : "transparent",
                  color: sortBy === s ? "#818cf8" : "#64748b",
                  transition: "all 0.15s",
                }}
              >
                {s === "roomNumber" ? "Room #" : s}
              </button>
            ))}
          </div>
        </div>

        {/* ── Error ── */}
        {error && (
          <div style={{
            padding: "12px 16px", borderRadius: 10, marginBottom: 20,
            background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.25)",
            color: "#f87171", fontSize: 13,
          }}>
            {error}
          </div>
        )}

        {/* ── Room Grid ── */}
        {loading ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "80px 0", gap: 14 }}>
            <div style={{
              width: 36, height: 36, borderRadius: "50%",
              border: "3px solid rgba(99,102,241,0.15)",
              borderTop: "3px solid #818cf8",
              animation: "spin 0.8s linear infinite",
            }} />
            <span style={{ color: "rgba(242,235,217,0.45)", fontSize: 14 }}>Loading rooms…</span>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{
            display: "flex", flexDirection: "column", alignItems: "center",
            padding: "80px 0", gap: 10, color: "rgba(242,235,217,0.45)",
          }}>
            <span style={{ fontSize: 36 }}><FontAwesomeIcon icon={faBed} /></span>
            <p style={{ margin: 0, fontSize: 14 }}>No rooms match your filters</p>
            {(statusFilter !== "all" || search) && (
              <button
                onClick={() => { setStatusFilter("all"); setSearch(""); }}
                style={{
                  marginTop: 8, padding: "8px 16px", borderRadius: 8, fontSize: 12,
                  background: "rgba(201,168,76,0.12)", border: "1px solid #e2e8f0",
                  color: "rgba(242,235,217,0.45)", cursor: "pointer",
                }}
              >
                Clear Filters
              </button>
            )}
          </div>
        ) : (
          <>
            <p style={{ margin: "0 0 14px", fontSize: 12, color: "rgba(242,235,217,0.45)" }}>
              Showing {filtered.length} room{filtered.length !== 1 ? "s" : ""}
              {statusFilter !== "all" ? ` · ${STATUS_META[statusFilter]?.label ?? statusFilter}` : ""}
            </p>
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
              gap: 14,
            }}>
              {filtered.map(task => (
                <RoomCard
                  key={task.taskId ?? task.roomId}
                  task={task}
                  onOpen={setModalTask}
                  onCycle={handleCycle}
                />
              ))}
            </div>
          </>
        )}

        {/* ── Update Modal ── */}
        {modalTask && (
          <UpdateModal
            task={modalTask}
            authHeaders={authHeaders}
            onClose={() => setModalTask(null)}
            onSaved={fetchAll}
          />
        )}
      </div>
    </DashboardLayout>
  );
}
